/**
 * CandidateAccordion — single source of truth for the candidate detail surface.
 *
 * Renders the candidate as a vertical list of collapsible accordion sections
 * with three power-user affordances:
 *   1. Sticky in-page search that auto-expands all sections so EVERY field is
 *      searchable, marks every match with <mark data-pwsearch>, and ships
 *      prev/next navigation + a 1/N counter. scrollIntoView walks every
 *      overflow ancestor so it works inside the dialog scroll container.
 *   2. Sticky section quick-nav: horizontal pill bar of section names,
 *      auto-highlights the one in view (IntersectionObserver) and click-jumps
 *      to it (auto-expands if collapsed).
 *   3. Expand-all / Collapse-all + per-candidate localStorage persistence.
 *
 * To make search work across sections without API-storming on every open, we
 * LAZY-MOUNT child content on first expand and then keep it mounted (hidden
 * via CSS) so it's searchable later. The "Expand all" button (and starting
 * a search) trigger lazy-mount of any section the user hasn't visited yet.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown, ChevronRight, ChevronLeft, Eye, EyeOff, X } from "lucide-react";
import { API } from "@/lib/api";
import ActivityFeed from "@/components/ActivityFeed";
import CandidateRatings from "@/components/candidate/CandidateRatings";
import CandidateNotes from "@/components/candidate/CandidateNotes";
import CandidateCompensation from "@/components/candidate/CandidateCompensation";
import BackgroundCheck from "@/components/candidate/BackgroundCheck";
import PersonalDocuments from "@/components/candidate/PersonalDocuments";
import CandidateInterviews from "@/components/candidate/CandidateInterviews";
import CandidateFollowups from "@/components/candidate/CandidateFollowups";
import CandidateVersions from "@/components/candidate/CandidateVersions";
import ResumeDiffPanel from "@/components/candidate/ResumeDiffPanel";
import InterviewQA from "@/components/candidate/InterviewQA";
import ScreeningProfileEditor from "@/components/candidate/ScreeningProfileEditor";
import CandidateOnlinePresence from "@/components/candidate/CandidateOnlinePresence";
import CandidatePortfolio from "@/components/candidate/CandidatePortfolio";
import CandidateContactBar from "@/components/candidate/CandidateContactBar";
import { AnalysisPanel, TrustScorePanel } from "@/components/CandidateProfileDialog";
import { useAuth } from "@/context/AuthContext";
import ScoreBadge from "@/components/ScoreBadge";
import CandidateVoteBar from "@/components/CandidateVoteBar";
import CandidateAssignments from "@/components/candidate/CandidateAssignments";
import UnifiedTimeline from "@/components/candidate/UnifiedTimeline";
import { ScreeningScorecardStandalone } from "@/components/candidate/ScreeningScorecard";
import TimelineList from "@/components/TimelineList";

function buildSections(c, opts) {
    const { isInterviewer, focusedApp, onReloaded, qaApp, currentUserId } = opts;
    const apps = c.applications || [];
    const sections = [
        { id: "analysis", title: "AI Analysis", defaultOpen: true,
            render: () => <AnalysisPanel candidate={c} apps={apps} focusedApp={focusedApp} onReloaded={onReloaded} /> },
        { id: "overview", title: "Overview", defaultOpen: true,
            render: () => <OverviewBody c={c} isInterviewer={isInterviewer} onPatched={onReloaded} /> },
        !isInterviewer && { id: "compensation", title: "Compensation",
            render: () => <CandidateCompensation candidateId={c.id} /> },
        !isInterviewer && { id: "bgv", title: "Background verification",
            render: () => <BackgroundCheck candidateId={c.id} /> },
        !isInterviewer && { id: "onboarding_docs", title: "Onboarding documents",
            render: () => <PersonalDocuments candidateId={c.id} /> },
        { id: "followups", title: "Follow-ups",
            render: () => <CandidateFollowups candidateId={c.id} /> },
        { id: "ratings", title: "Ratings",
            render: () => <CandidateRatings candidateId={c.id} currentUserId={currentUserId} candidateSkills={c.skills || []} /> },
        { id: "interviews", title: "Interviews",
            render: () => <CandidateInterviews candidateId={c.id} /> },
        { id: "qa", title: "Interview Q&A",
            render: () => <QABody apps={apps} qaApp={qaApp} /> },
        { id: "notes", title: "Notes",
            render: () => <CandidateNotes candidateId={c.id} currentUserId={currentUserId} isOwner={opts.isOwner} /> },
        { id: "resume", title: "Resume",
            render: () => <CandidateVersions candidateId={c.id} onUpdated={onReloaded} /> },
        { id: "diff", title: "Resume diff",
            render: () => <ResumeDiffPanel candidateId={c.id} /> },
        { id: "jobs", title: "Applications",
            render: () => <ApplicationsBody c={c} /> },
        { id: "activity", title: "Activity",
            render: () => <ActivityFeed targetType="candidate" targetId={c.id} /> },
    ];
    return sections.filter(Boolean);
}


export default function CandidateAccordion({ candidate, focusedApp, qaApp, onReloaded, currentUserId, isOwner, storageKey }) {
    const { user } = useAuth();
    const isInterviewer = user?.role === "interviewer";
    const sections = useMemo(
        () => buildSections(candidate, { isInterviewer, focusedApp, qaApp, onReloaded, currentUserId, isOwner }),
        [candidate, isInterviewer, focusedApp, qaApp, onReloaded, currentUserId, isOwner],
    );

    const lsKey = `pw-accordion-${storageKey || candidate.id}`;
    const [openMap, setOpenMap] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(lsKey) || "null");
            if (saved && typeof saved === "object") return saved;
        } catch { /* ignore */ }
        const init = {};
        sections.forEach((s) => { init[s.id] = !!s.defaultOpen; });
        return init;
    });
    // Sections that have ever been opened (so we keep their content mounted
    // and searchable even after the user collapses them again).
    const [mountedMap, setMountedMap] = useState(() => ({ ...openMap }));
    useEffect(() => {
        try { localStorage.setItem(lsKey, JSON.stringify(openMap)); } catch { /* ignore */ }
    }, [openMap, lsKey]);

    const toggle = (id) => {
        setOpenMap((m) => ({ ...m, [id]: !m[id] }));
        setMountedMap((m) => ({ ...m, [id]: true }));
    };
    const expandAll = () => {
        const next = {};
        sections.forEach((s) => { next[s.id] = true; });
        setOpenMap(next);
        setMountedMap(next);
    };
    const collapseAll = () => {
        const next = {};
        sections.forEach((s) => { next[s.id] = false; });
        setOpenMap(next);
    };

    // ─── In-page search ───────────────────────────────────────────────────
    const containerRef = useRef(null);
    const [query, setQuery] = useState("");
    const [matchTotal, setMatchTotal] = useState(0);
    const [activeMatch, setActiveMatch] = useState(0); // 0-based index

    // When user types into search, force-mount + expand every section so the
    // search can scan all content. We only DO this when query becomes
    // non-empty (so we don't fight a user who collapsed sections deliberately).
    const wasSearching = useRef(false);
    useEffect(() => {
        const searching = !!query.trim();
        if (searching && !wasSearching.current) {
            expandAll();
        }
        wasSearching.current = searching;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    // ── Search engine ─────────────────────────────────────────────────────
    const clearHighlights = useCallback(() => {
        if (!containerRef.current) return;
        containerRef.current.querySelectorAll("mark[data-pwsearch]").forEach((el) => {
            const p = el.parentNode;
            if (!p) return;
            p.replaceChild(document.createTextNode(el.textContent || ""), el);
            p.normalize();
        });
    }, []);

    const runSearch = useCallback((q) => {
        clearHighlights();
        if (!containerRef.current || !q || q.length < 2) {
            setMatchTotal(0); setActiveMatch(0);
            return;
        }
        const needle = q.toLowerCase();
        const walker = document.createTreeWalker(containerRef.current, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => {
                if (!node.nodeValue || !node.nodeValue.toLowerCase().includes(needle)) return NodeFilter.FILTER_REJECT;
                let p = node.parentNode;
                while (p && p !== containerRef.current) {
                    const tag = p.tagName;
                    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SCRIPT" || tag === "STYLE") return NodeFilter.FILTER_REJECT;
                    // Don't search inside the sticky section nav bar
                    if (p.dataset && p.dataset.pwSearchSkip) return NodeFilter.FILTER_REJECT;
                    p = p.parentNode;
                }
                return NodeFilter.FILTER_ACCEPT;
            },
        });
        const matchNodes = [];
        let node;
        while ((node = walker.nextNode())) matchNodes.push(node);
        let count = 0;
        matchNodes.forEach((textNode) => {
            const txt = textNode.nodeValue;
            const lower = txt.toLowerCase();
            const frag = document.createDocumentFragment();
            let i = 0;
            while (i < txt.length) {
                const found = lower.indexOf(needle, i);
                if (found === -1) { frag.appendChild(document.createTextNode(txt.slice(i))); break; }
                if (found > i) frag.appendChild(document.createTextNode(txt.slice(i, found)));
                const m = document.createElement("mark");
                m.setAttribute("data-pwsearch", "1");
                m.style.background = "#fff36b";
                m.style.color = "inherit";
                m.style.padding = "0 1px";
                m.textContent = txt.slice(found, found + needle.length);
                frag.appendChild(m);
                count += 1;
                i = found + needle.length;
            }
            textNode.parentNode.replaceChild(frag, textNode);
        });
        setMatchTotal(count);
        setActiveMatch(0);
    }, [clearHighlights]);

    // Debounced search — DOES NOT depend on openMap to avoid the loop where
    // auto-expand → rerun → clear marks. Re-runs after lazy-mount happens via
    // a separate effect that watches mountedMap so newly-mounted DOM nodes
    // get scanned exactly once.
    useEffect(() => {
        const t = setTimeout(() => runSearch(query), 200);
        return () => clearTimeout(t);
    }, [query, runSearch]);

    // After lazy-mount of newly-opened sections, re-scan once so their text
    // is also marked. This fires only when mountedMap or openMap changes,
    // not as a side-effect of the marker insertion.
    useEffect(() => {
        if (!query.trim()) return;
        const t = setTimeout(() => runSearch(query), 50);
        return () => clearTimeout(t);
    }, [mountedMap, openMap, query, runSearch]);

    // Highlight + scroll active match
    useEffect(() => {
        if (!containerRef.current) return;
        const all = containerRef.current.querySelectorAll("mark[data-pwsearch]");
        all.forEach((m, i) => {
            if (i === activeMatch) {
                m.style.outline = "2px solid #f43f5e";
                m.style.background = "#ffd166";
                // Scroll the mark into view inside whichever scrollable ancestor
                // we live in (dialog body OR window).
                try { m.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* ignore */ }
            } else {
                m.style.outline = "";
                m.style.background = "#fff36b";
            }
        });
    }, [activeMatch, matchTotal]);

    const step = (delta) => {
        if (matchTotal === 0) return;
        setActiveMatch((i) => ((i + delta) % matchTotal + matchTotal) % matchTotal);
    };

    // ── Sticky section quick-nav (active = section currently in view) ─────
    const sectionRefs = useRef({});
    const [activeSection, setActiveSection] = useState(sections[0]?.id);
    // Phase 150 — when the recruiter clicks a nav pill, we lock the active
    // section to the clicked id for the duration of the smooth scroll (and a
    // short settle window) so the IntersectionObserver doesn't briefly flip
    // the highlight to whichever section lands under the viewport-top.
    const navLockRef = useRef({ id: null, until: 0 });
    useEffect(() => {
        const io = new IntersectionObserver(
            (entries) => {
                if (navLockRef.current.id && Date.now() < navLockRef.current.until) {
                    // Lock in effect — ignore the observer during the smooth scroll.
                    return;
                }
                // Pick the most-visible entry near the top
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible[0]) setActiveSection(visible[0].target.dataset.sectionId);
            },
            { rootMargin: "-30% 0px -55% 0px", threshold: 0.01 },
        );
        sections.forEach((s) => {
            const el = sectionRefs.current[s.id];
            if (el) io.observe(el);
        });
        return () => io.disconnect();
    }, [sections]);

    const jumpTo = (id) => {
        setOpenMap((m) => ({ ...m, [id]: true }));
        setMountedMap((m) => ({ ...m, [id]: true }));
        setActiveSection(id);  // synchronous highlight so the click feels immediate
        // Lock the IntersectionObserver out for ~900ms — long enough for the
        // smooth scroll to settle on all platforms.
        navLockRef.current = { id, until: Date.now() + 900 };
        // Briefly "focus" the section header so the recruiter sees they jumped here.
        setFocusedSection(id);
        if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
        focusTimerRef.current = setTimeout(() => setFocusedSection(null), 2200);
        requestAnimationFrame(() => {
            const el = sectionRefs.current[id];
            if (!el) return;
            // Find the nearest scrollable ancestor and scroll inside it, rather
            // than falling back to scrollIntoView which can bubble up to parent
            // overflow:hidden boxes (iter148 — dialog scroll leak).
            let scroller = el.parentElement;
            while (scroller && scroller !== document.body) {
                const cs = getComputedStyle(scroller);
                if (/(auto|scroll)/.test(cs.overflowY) && scroller.scrollHeight > scroller.clientHeight) break;
                scroller = scroller.parentElement;
            }
            if (scroller && scroller !== document.body) {
                // 48px fudge to clear the sticky section-nav strip
                const top = el.offsetTop - scroller.offsetTop - 48;
                scroller.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
            } else {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        });
    };

    // Focus-state machine — set when (1) recruiter clicks a pill, (2) recruiter
    // expands a collapsed section, or (3) recruiter focuses any input/textarea
    // inside a section. The active pill above also reflects this state.
    const [focusedSection, setFocusedSection] = useState(null);
    const focusTimerRef = useRef(null);
    useEffect(() => () => { if (focusTimerRef.current) clearTimeout(focusTimerRef.current); }, []);
    useEffect(() => {
        if (!containerRef.current) return;
        const onFocus = (e) => {
            // Walk up the DOM to find the closest section
            let n = e.target;
            while (n && n !== containerRef.current) {
                if (n.dataset && n.dataset.sectionId) {
                    setFocusedSection(n.dataset.sectionId);
                    setActiveSection(n.dataset.sectionId);
                    return;
                }
                n = n.parentNode;
            }
        };
        containerRef.current.addEventListener("focusin", onFocus);
        return () => containerRef.current?.removeEventListener("focusin", onFocus);
    }, []);

    const toggleWithFocus = (id) => {
        // expand → also activate that section
        setOpenMap((m) => {
            const opening = !m[id];
            if (opening) {
                setFocusedSection(id);
                setActiveSection(id);
                if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
                focusTimerRef.current = setTimeout(() => setFocusedSection(null), 2200);
            }
            return { ...m, [id]: !m[id] };
        });
        setMountedMap((m) => ({ ...m, [id]: true }));
    };

    return (
        <div ref={containerRef} className="space-y-2 sm:space-y-3" data-testid="candidate-accordion">
            {/* ─── Sticky search + bulk controls + section quick-nav ─── */}
            <div data-pw-search-skip="1" className="sticky top-0 z-30 -mx-3 sm:-mx-6 px-3 sm:px-6 py-2 bg-white border-b border-border shadow-sm" data-testid="candidate-sticky-header">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5 flex-1 min-w-[180px] border border-border bg-white px-2 h-9">
                        <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); step(e.shiftKey ? -1 : 1); }
                                if (e.key === "Escape") setQuery("");
                            }}
                            placeholder="Search candidate (text, skills, answers, notes…)"
                            className="flex-1 text-sm bg-transparent outline-none"
                            data-testid="candidate-accordion-search"
                            aria-label="Search candidate page"
                        />
                        {query && (
                            <button onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground" aria-label="Clear search" data-testid="candidate-accordion-search-clear">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {query && (
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground shrink-0" data-testid="candidate-accordion-match-count">
                                {matchTotal === 0 ? "0/0" : `${activeMatch + 1}/${matchTotal}`}
                            </span>
                        )}
                        {matchTotal > 0 && (
                            <div className="flex items-center gap-0.5 shrink-0">
                                <button onClick={() => step(-1)} className="px-1 py-1 hover:bg-secondary" aria-label="Previous match" data-testid="candidate-accordion-search-prev"><ChevronLeft className="w-3.5 h-3.5" /></button>
                                <button onClick={() => step(1)} className="px-1 py-1 hover:bg-secondary" aria-label="Next match" data-testid="candidate-accordion-search-next"><ChevronRight className="w-3.5 h-3.5" /></button>
                            </div>
                        )}
                    </div>
                    <button onClick={expandAll} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 h-9 hover:bg-secondary inline-flex items-center gap-1" data-testid="candidate-accordion-expand-all">
                        <Eye className="w-3 h-3" /> Expand all
                    </button>
                    <button onClick={collapseAll} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 h-9 hover:bg-secondary inline-flex items-center gap-1" data-testid="candidate-accordion-collapse-all">
                        <EyeOff className="w-3 h-3" /> Collapse all
                    </button>
                </div>
                {/* Section pill bar — horizontal scroll on mobile */}
                <div className="mt-2 -mx-1 px-1 flex gap-1 overflow-x-auto no-scrollbar" data-testid="candidate-section-nav">
                    {sections.map((s) => {
                        const active = activeSection === s.id;
                        return (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => jumpTo(s.id)}
                                className={`text-[10px] font-mono uppercase tracking-widest shrink-0 px-2 py-1 border whitespace-nowrap transition-colors ${
                                    active
                                        ? "bg-ikb text-white border-ikb"
                                        : "bg-white text-foreground border-border hover:bg-secondary"
                                }`}
                                data-testid={`candidate-section-nav-${s.id}`}
                                aria-current={active ? "true" : undefined}
                            >
                                {s.title}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ─── Sections ─── */}
            {sections.map((s) => {
                const open = !!openMap[s.id];
                const mounted = !!mountedMap[s.id];
                const focused = focusedSection === s.id || activeSection === s.id;
                return (
                    <section
                        key={s.id}
                        ref={(el) => { sectionRefs.current[s.id] = el; }}
                        data-section-id={s.id}
                        data-testid={`candidate-section-${s.id}`}
                        data-focused={focused ? "1" : "0"}
                        className={`bg-white scroll-mt-32 transition-colors border ${focused ? "border-ikb shadow-[0_0_0_1px_rgba(0,47,167,0.6)]" : "border-border"}`}
                    >
                        <button
                            type="button"
                            onClick={() => toggleWithFocus(s.id)}
                            aria-expanded={open}
                            data-testid={`candidate-section-toggle-${s.id}`}
                            className={`w-full flex items-center justify-between px-3 sm:px-4 py-2.5 text-left border-b ${focused ? "bg-blue-50/60 border-ikb/40" : "hover:bg-secondary/50 border-border"}`}
                        >
                            <span className="font-display font-bold text-sm sm:text-base inline-flex items-center gap-2">
                                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                {s.title}
                                {focused && <span className="text-[9px] font-mono uppercase tracking-widest text-ikb bg-blue-50 border border-ikb/40 px-1.5 py-px ml-1">Active</span>}
                            </span>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{open ? "Open" : "Tap to open"}</span>
                        </button>
                        {/* Always render once mounted (kept in DOM for search), hide visually when collapsed */}
                        {mounted && (
                            <div className={`p-3 sm:p-4 ${open ? "" : "hidden"}`}>
                                {s.render()}
                            </div>
                        )}
                    </section>
                );
            })}
        </div>
    );
}

/* ─── Overview body — shared between dialog & full page ─── */
function OverviewBody({ c, isInterviewer, onPatched }) {
    return (
        <div className="space-y-5 sm:space-y-6">
            {!isInterviewer && <CandidateContactBar candidate={c} onUpdated={onPatched} />}
            {!isInterviewer && <TrustScorePanel cid={c.id} />}
            {!isInterviewer && (
                <ScreeningScorecardStandalone profile={c.screening_profile || {}} />
            )}
            {c.ai_summary && (
                <section>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb mb-1">AI summary</div>
                    <p className="text-sm sm:text-base leading-relaxed">{c.ai_summary}</p>
                </section>
            )}
            <section className="grid grid-cols-3 gap-px bg-border">
                <div className="bg-white p-3 sm:p-4">
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Experience</div>
                    <div className="font-display font-black text-xl sm:text-2xl mt-1">{c.total_experience_years || 0}<span className="text-muted-foreground text-xs sm:text-sm"> yrs</span></div>
                </div>
                <div className="bg-white p-3 sm:p-4">
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Skills</div>
                    <div className="font-display font-black text-xl sm:text-2xl mt-1">{(c.skills || []).length}</div>
                </div>
                <div className="bg-white p-3 sm:p-4">
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Applications</div>
                    <div className="font-display font-black text-xl sm:text-2xl mt-1">{(c.applications || []).length}</div>
                </div>
            </section>
            {(c.skills || []).length > 0 && (
                <section>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-2">Skills</div>
                    <div className="flex flex-wrap gap-1.5">
                        {c.skills.map((s) => <span key={s} className="text-[11px] sm:text-xs px-2 py-0.5 border border-border bg-secondary font-mono">{s}</span>)}
                    </div>
                </section>
            )}
            {/* AI-inferred Hard Skills Snapshot — hidden from interviewer view */}
            {!isInterviewer && ((c.inferred_hard_skills || []).length > 0 || (c.outdated_skill_signals || []).length > 0 || (c.modern_must_knows_missing || []).length > 0) && (
                <section data-testid="hard-skills-snapshot">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Hard skills snapshot · AI-inferred</span>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                        <div className="border border-violet-200 bg-violet-50/40" data-testid="hss-inferred">
                            <div className="px-3 py-2 border-b border-violet-200 bg-violet-50 flex items-center justify-between">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-violet-800 font-semibold">Likely possesses</span>
                                <span className="text-[9px] font-mono text-violet-700/70">{(c.inferred_hard_skills || []).length}</span>
                            </div>
                            {(c.inferred_hard_skills || []).length === 0 ? (
                                <div className="px-3 py-3 text-[11px] text-foreground/40">—</div>
                            ) : (
                                <ul className="divide-y divide-violet-100">
                                    {(c.inferred_hard_skills || []).map((it, i) => (
                                        <li key={`${it.skill}-${i}`} className="px-3 py-2" data-testid={`hss-inf-${i}`}>
                                            <div className="text-sm font-semibold text-violet-900">{it.skill}</div>
                                            {it.reason && <div className="text-[11px] text-foreground/70 mt-0.5 leading-snug">{it.reason}</div>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="border border-amber-200 bg-amber-50/40" data-testid="hss-modern">
                            <div className="px-3 py-2 border-b border-amber-200 bg-amber-50 flex items-center justify-between">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-amber-800 font-semibold">Modern must-knows missing</span>
                                <span className="text-[9px] font-mono text-amber-700/70">{(c.modern_must_knows_missing || []).length}</span>
                            </div>
                            {(c.modern_must_knows_missing || []).length === 0 ? (
                                <div className="px-3 py-3 text-[11px] text-foreground/40">—</div>
                            ) : (
                                <ul className="divide-y divide-amber-100">
                                    {(c.modern_must_knows_missing || []).map((s, i) => (
                                        <li key={`${s}-${i}`} className="px-3 py-2 text-sm text-amber-900" data-testid={`hss-mod-${i}`}>{s}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="border border-rose-200 bg-rose-50/40" data-testid="hss-outdated">
                            <div className="px-3 py-2 border-b border-rose-200 bg-rose-50 flex items-center justify-between">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-rose-800 font-semibold">Outdated signals</span>
                                <span className="text-[9px] font-mono text-rose-700/70">{(c.outdated_skill_signals || []).length}</span>
                            </div>
                            {(c.outdated_skill_signals || []).length === 0 ? (
                                <div className="px-3 py-3 text-[11px] text-foreground/40">—</div>
                            ) : (
                                <ul className="divide-y divide-rose-100">
                                    {(c.outdated_skill_signals || []).map((s, i) => (
                                        <li key={`${s}-${i}`} className="px-3 py-2 text-sm text-rose-900" data-testid={`hss-out-${i}`}>{s}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    {c.hard_skills_enriched_at && (
                        <div className="text-[10px] font-mono text-foreground/40 mt-2">Last enriched: {new Date(c.hard_skills_enriched_at).toLocaleString()}</div>
                    )}
                </section>
            )}
            {((c.experience || []).length > 0 || (c.education || []).length > 0) && (
                <UnifiedTimeline candidate={c} onNotesRefresh={onPatched} />
            )}
            {(c.certifications || []).length > 0 && (
                <section>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-2">Certifications</div>
                    <ul className="space-y-1.5">
                        {c.certifications.map((cert, i) => (
                            <li key={i} className="text-xs font-mono border border-border bg-white px-2.5 py-1.5">
                                <span className="font-bold">{cert.name || "—"}</span>
                                {cert.issuer && <span className="text-muted-foreground"> · {cert.issuer}</span>}
                                {cert.issued_at && <span className="text-muted-foreground"> · {cert.issued_at}</span>}
                            </li>
                        ))}
                    </ul>
                </section>
            )}
            {!isInterviewer && (
                <ScreeningProfileEditor
                    candidateId={c.id}
                    initial={c.screening_profile || {}}
                    initialMeta={c.screening_profile_meta || {}}
                    onSaved={onPatched}
                />
            )}
            {!isInterviewer && <CandidateOnlinePresence candidateId={c.id} />}
            {!isInterviewer && <CandidatePortfolio candidateId={c.id} />}
            {!isInterviewer && <CandidateAssignments candidateId={c.id} />}
        </div>
    );
}

/* ─── Interview Q&A body with multi-app picker ─── */
function QABody({ apps, qaApp }) {
    const [activeId, setActiveId] = useState(qaApp?.id || apps?.[0]?.id || null);
    useEffect(() => { setActiveId(qaApp?.id || apps?.[0]?.id || null); }, [qaApp?.id, apps]);
    if (!apps || apps.length === 0) {
        return <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Add the candidate to a job to start an interview Q&amp;A.</div>;
    }
    const active = apps.find((a) => a.id === activeId) || apps[0];
    return (
        <div className="space-y-4">
            {apps.length > 1 && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="cpd-qa-app-picker">
                    <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Application:</span>
                    {apps.map((a) => (
                        <button key={a.id} type="button" onClick={() => setActiveId(a.id)}
                            className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 border ${active?.id === a.id ? "bg-ikb text-white border-ikb" : "bg-white border-border hover:bg-secondary"}`}
                            data-testid={`cpd-qa-app-${a.id}`}
                        >
                            {a.job_title || "Job"}
                        </button>
                    ))}
                </div>
            )}
            {active ? <InterviewQA applicationId={active.id} /> : null}
        </div>
    );
}

/* ─── Applications body ─── */
function ApplicationsBody({ c }) {
    const apps = c.applications || [];
    if (apps.length === 0) {
        return <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Not assigned to any jobs yet.</div>;
    }
    return (
        <div className="border border-border bg-white divide-y divide-border" data-testid="cpd-applications-list">
            {apps.map((a) => (
                <div key={a.id} data-testid={`cpd-app-row-${a.id}`} className="flex items-center justify-between p-4 hover:bg-secondary gap-4">
                    <Link to={`/jobs/${a.job_id}`} className="flex-1 min-w-0">
                        <div className="font-medium truncate">{a.job_title || "Job"}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                            Applied {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}
                            {a.source ? ` · ${a.source}` : ""}
                            {a.stage ? ` · ${a.stage}` : ""}
                        </div>
                    </Link>
                    <CandidateVoteBar applicationId={a.id} size="sm" />
                    <ScoreBadge
                        score={a.match_score || 0}
                        size="sm"
                        processing={a.scoring_status === "processing"}
                        status={a.scoring_status === "ai_unavailable" ? "ai_unavailable" : null}
                    />
                </div>
            ))}
        </div>
    );
}
